import type { StreamChatOptions, StreamChunk, Agent } from './types'
import { API_CONFIG } from '@/shared/config'

export const agentplatService = {
  /**
   * Stream chat with an agent using NDJSON format
   */
  async streamChat({
    agentId,
    messages,
    context,
    onChunk,
    signal,
  }: StreamChatOptions): Promise<{ threadId: string | null }> {
    const streamApiUrl =
      import.meta.env.VITE_STREAM_API_URL || import.meta.env.VITE_AGENTPLAT_STREAM_URL || ''
    if (!streamApiUrl) {
      throw new Error('Stream API URL not configured')
    }

    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()
    if (!token) {
      throw new Error('Not authenticated')
    }

    const url = `${streamApiUrl.replace(/\/+$/, '')}/agents/${agentId}/chat/stream`

    const payload: { messages: typeof messages; context?: Record<string, unknown> } = {
      messages,
    }
    if (context && Object.keys(context).length > 0) {
      payload.context = context
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `AgentPlat error: ${response.status}`)
    }

    if (!response.body) {
      throw new Error('No response body')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let resultThreadId: string | null = null

    let done = false
    while (!done) {
      const result = await reader.read()
      done = result.done

      if (result.value) {
        buffer += decoder.decode(result.value, { stream: !done })
      }

      // NDJSON: each line is a complete JSON object
      let newlineIndex: number
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim()
        buffer = buffer.slice(newlineIndex + 1)

        if (!line) continue

        try {
          const chunk: StreamChunk = JSON.parse(line)

          if (chunk.threadId && !resultThreadId) {
            resultThreadId = chunk.threadId
          }

          onChunk(chunk)

          if (chunk.done) {
            return { threadId: resultThreadId }
          }
        } catch {
          console.warn('[agentplatService] Failed to parse NDJSON line:', line)
        }
      }
    }

    // Process any remaining buffer content (final chunk without trailing newline)
    const remaining = buffer.trim()
    if (remaining) {
      try {
        const chunk: StreamChunk = JSON.parse(remaining)

        if (chunk.threadId && !resultThreadId) {
          resultThreadId = chunk.threadId
        }

        onChunk(chunk)
      } catch {
        console.warn('[agentplatService] Failed to parse final NDJSON chunk:', remaining)
      }
    }

    return { threadId: resultThreadId }
  },

  /**
   * List all agents for a specific tenant via backend API
   * @param role Optional role filter to filter agents by role
   */
  async listAgents(role?: string): Promise<Agent[]> {
    // Call our backend API which will proxy to AgentPlat with the secure API key
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    // Build URL with optional role query parameter
    let url = `${apiUrl}/agents`
    if (role) {
      url += `?role=${encodeURIComponent(role)}`
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Backend API error: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  },

  /**
   * List all agents from the marketplace (principal tenant) via backend API
   * @param role Optional role filter to filter agents by role
   */
  async listMarketAgents(role?: string): Promise<Agent[]> {
    // Call our backend API which will proxy to AgentPlat with the principal tenant ID
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    // Build URL with optional role query parameter
    let url = `${apiUrl}/agents/market`
    if (role) {
      url += `?role=${encodeURIComponent(role)}`
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Backend API error: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  },

  /**
   * Create a new agent draft via backend API
   */
  async saveAgentDraft(data: { 
    name: string
    description: string
    avatar?: string
    systemInstructions?: string
    agentId?: string // For updates
  }): Promise<{ success: boolean; agent?: unknown; error?: string }> {
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    const method = data.agentId ? 'PUT' : 'POST'
    const response = await fetch(`${apiUrl}/agents/draft`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[agentplatService] saveAgentDraft error:', errorData)
      throw new Error(errorData.error || `Backend API error: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  },

  /**
   * Upload a file for an agent
   */
  async uploadAgentFile(agentId: string, file: File): Promise<{ fileKey: string; filename: string; url: string }> {
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${apiUrl}/agents/${agentId}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[agentplatService] uploadAgentFile error:', errorData)
      throw new Error(errorData.error || `Backend API error: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  },

  /**
   * Publish an agent draft to AgentPlat
   */
  async publishAgent(agentId: string): Promise<{ agentId: string; agentPlatId: string; status: string }> {
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    const response = await fetch(`${apiUrl}/agents/${agentId}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[agentplatService] publishAgent error:', errorData)
      throw new Error(errorData.error || `Backend API error: ${response.status}`)
    }

    const result = await response.json()
    return result.data
  },

  /**
   * Invoke an agent with a one-shot prompt (non-streaming)
   * Results are cached in DynamoDB — same prompt returns cached response.
   * Pass regenerate=true to force a fresh AI call.
   * @param agentId - The agent ID to invoke
   * @param content - The text prompt
   * @param filesUrl - Optional array of media URLs (images/videos) to analyze
   * @param regenerate - If true, skip cache and generate fresh analysis
   */
  async invokeAgent(agentId: string, content: string, filesUrl?: string[], regenerate = false): Promise<{ content: string; threadId?: string; fromCache?: boolean }> {
    const apiUrl = API_CONFIG.baseUrl

    // Get auth token from Amplify
    const { getAuthToken } = await import('@trafiflow/shared-ui')
    const token = await getAuthToken()

    const body: { content: string; filesUrl?: string[]; regenerate?: boolean } = { content }
    if (filesUrl && filesUrl.length > 0) {
      body.filesUrl = filesUrl
    }
    if (regenerate) {
      body.regenerate = true
    }

    const response = await fetch(`${apiUrl}/agents/${agentId}/invoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[agentplatService] invokeAgent error:', errorData)
      throw new Error(errorData.error?.message || errorData.error || `Invoke error: ${response.status}`)
    }

    const result = await response.json()
    return {
      content: result.data?.content || '',
      threadId: result.data?.threadId,
      fromCache: result.from_cache || false,
    }
  },

  /**
   * Check if AgentPlat service is reachable
   */
  async checkHealth(): Promise<boolean> {
    try {
      const streamApiUrl =
        import.meta.env.VITE_STREAM_API_URL || import.meta.env.VITE_AGENTPLAT_STREAM_URL || ''
      if (!streamApiUrl) {
        return false
      }
      const response = await fetch(`${streamApiUrl.replace(/\/+$/, '')}/health`)
      return response.ok
    } catch {
      return false
    }
  },
} as const
